<template>
  <div class="admin-payment-settings">
    <v-row>
      <v-col cols="12" md="8">
    <v-card class="pa-6 rounded-lg" elevation="2">
      <div class="d-flex align-center mb-6">
        <v-icon color="#d6230b" size="32" class="mr-3">mdi-qrcode</v-icon>
        <h2 class="text-h5 font-weight-bold">ตั้งค่า PromptPay สำหรับสร้าง QR Code</h2>
      </div>

      <v-divider class="mb-6"></v-divider>

      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="settings.promptPayId"
              label="PromptPay ID (เบอร์โทร 10 หลัก / เลขบัตร 13 หลัก / เลขบัญชี)"
              outlined
              dense
              required
              :rules="[v => !!v || 'กรุณาระบุ PromptPay ID']"
              hint="ใช้สำหรับ generate QR code ในการชำระเงินของระบบ"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="settings.firstNameTh"
              label="ชื่อจริงผู้รับเงิน (ภาษาไทย)"
              outlined
              dense
              hint="ใช้ตรวจสอบชื่อผู้รับเงินบนสลิป"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="settings.lastNameTh"
              label="นามสกุลผู้รับเงิน (ภาษาไทย)"
              outlined
              dense
              hint="ใช้ตรวจสอบชื่อผู้รับเงินบนสลิป"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12" class="d-flex justify-center mt-4">
            <v-btn
              color="#d6230b"
              class="white--text px-10 rounded-lg"
              large
              :loading="loading"
              :disabled="!valid"
              @click="saveSettings"
            >
              <v-icon left>mdi-content-save</v-icon>
              บันทึกการตั้งค่า
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- Custom QR Code upload section -->
    <v-card class="pa-6 rounded-lg mt-6" elevation="2">
      <div class="d-flex align-center mb-4">
        <v-icon color="#2563eb" size="32" class="mr-3">mdi-image-area</v-icon>
        <h2 class="text-h5 font-weight-bold">ใช้รูป QR Code ที่อัปโหลดเอง (ไม่บังคับ)</h2>
      </div>

      <v-alert type="info" text dense border="left" class="mb-4">
        <div class="text-body-2">
          ถ้าอัปโหลดรูป QR Code ที่นี่ ระบบจะใช้รูปนี้แทนการ generate QR อัตโนมัติ
          <strong>ในทุกหน้าที่มีการชำระเงิน</strong> (สั่งซื้อ, จองเดินทาง, เติม ELSONIII Cash)
          <br />
          หมายเหตุ: รูป QR ที่อัปโหลดจะไม่มียอดเงินฝังในตัว — ลูกค้าจะต้องกรอกยอดเองในแอปธนาคาร
        </div>
      </v-alert>

      <v-alert
        v-if="settings.qrCodeImage && !settings.qrLearnedAccountNumber && !settings.qrLearnedAccountName"
        type="error"
        text
        dense
        border="left"
        class="mb-4"
      >
        <div class="text-body-2 mb-2">
          ยังไม่ได้เก็บข้อมูลผู้รับสำหรับ QR นี้ — แนะนำให้
          <strong>ทดสอบจ่ายเอง 1 บาท</strong> แล้วอัปโหลดสลิปด้านล่าง
          เพื่อให้ระบบเรียนรู้ข้อมูลผู้รับก่อนเปิดให้ลูกค้าใช้งานจริง
        </div>
      </v-alert>

      <div
        v-else-if="settings.qrCodeImage && (settings.qrLearnedAccountNumber || settings.qrLearnedAccountName)"
        class="mb-4"
      >
        <v-alert type="success" text dense border="left" class="mb-3">
          <div class="text-body-2">
            เก็บข้อมูลผู้รับแล้ว — สามารถแก้ไขได้ถ้าชื่อไม่ครบ (สลิปมักตัดชื่อยาวๆ ทิ้ง)
          </div>
        </v-alert>

        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="settings.qrLearnedAccountName"
              label="ชื่อผู้รับ (ใช้ตรวจสลิป)"
              outlined
              dense
              hint="ระบบจะเทียบแบบ substring — สามารถพิมพ์ชื่อเต็มได้"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="settings.qrLearnedAccountNumber"
              label="เลขบัญชี / Biller ID"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="settings.qrLearnedAccountType"
              label="ประเภท"
              outlined
              dense
              hint="เช่น 02001 (PromptPay), 03001 (Biller)"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="d-flex justify-end" style="gap:10px;">
            <v-btn outlined color="grey darken-1" :disabled="loading" @click="fetchSettings">
              <v-icon left>mdi-refresh</v-icon>
              คืนค่า
            </v-btn>
            <v-btn color="#2563eb" dark :loading="loading" @click="saveLearnedReceiver">
              <v-icon left>mdi-content-save-edit</v-icon>
              บันทึกข้อมูลผู้รับ
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-row align="center">
        <v-col cols="12" md="5" class="text-center">
          <div class="qr-preview-box mx-auto" :class="{ clickable: !!qrImagePreview }" @click="qrImagePreview && openZoom(qrImagePreview)">
            <v-img
              v-if="qrImagePreview"
              :src="qrImagePreview"
              contain
              max-height="240"
              class="rounded"
            ></v-img>
            <div v-else class="empty-preview d-flex flex-column align-center justify-center">
              <v-icon size="60" color="grey lighten-1">mdi-qrcode</v-icon>
              <span class="grey--text mt-2">ยังไม่มีรูป QR Code</span>
            </div>
            <v-btn
              v-if="qrImagePreview"
              fab
              x-small
              color="white"
              class="zoom-fab"
              elevation="2"
              @click.stop="openZoom(qrImagePreview)"
            >
              <v-icon color="#2563eb">mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </div>
          <v-btn
            v-if="qrImagePreview"
            text
            small
            color="#2563eb"
            class="mt-2"
            @click="openZoom(qrImagePreview)"
          >
            <v-icon left small>mdi-fullscreen</v-icon>
            ดูรูปใหญ่ / สแกน QR
          </v-btn>
        </v-col>

        <v-col cols="12" md="7">
          <v-file-input
            v-model="qrFile"
            label="เลือกไฟล์รูป QR Code"
            accept="image/png, image/jpeg, image/webp"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-image"
            show-size
            :clearable="true"
            @change="onFileSelected"
          ></v-file-input>

          <div class="d-flex flex-wrap" style="gap: 12px;">
            <v-btn
              color="#2563eb"
              dark
              :loading="qrLoading"
              :disabled="!qrFile"
              @click="uploadQR"
            >
              <v-icon left>mdi-upload</v-icon>
              อัปโหลด
            </v-btn>
            <v-btn
              v-if="settings.qrCodeImage"
              color="error"
              outlined
              :loading="qrLoading"
              @click="confirmDelete = true"
            >
              <v-icon left>mdi-delete</v-icon>
              ลบรูปที่อัปโหลด (กลับไปใช้ QR อัตโนมัติ)
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Learn receiver from test slip -->
    <v-card v-if="settings.qrCodeImage" class="pa-6 rounded-lg mt-6" elevation="2">
      <div class="d-flex align-center mb-4">
        <v-icon color="#059669" size="32" class="mr-3">mdi-receipt-text-check-outline</v-icon>
        <h2 class="text-h5 font-weight-bold">ทดสอบ / เรียนรู้ข้อมูลผู้รับจากสลิป</h2>
      </div>

      <v-alert type="info" text dense border="left" class="mb-4">
        <div class="text-body-2">
          อัปโหลดสลิปที่ <strong>คุณจ่ายเอง</strong> ผ่าน QR ด้านบน (จำนวนเท่าใดก็ได้)
          ระบบจะดึงข้อมูลผู้รับจากสลิปมาบันทึกไว้
          เพื่อใช้ตรวจความถูกต้องของสลิปลูกค้าในอนาคต
        </div>
      </v-alert>

      <v-row align="center">
        <v-col cols="12" md="5" class="text-center">
          <div class="qr-preview-box mx-auto" :class="{ clickable: !!testSlipPreview }" @click="testSlipPreview && openZoom(testSlipPreview)">
            <v-img
              v-if="testSlipPreview"
              :src="testSlipPreview"
              contain
              max-height="240"
              class="rounded"
            ></v-img>
            <div v-else class="empty-preview d-flex flex-column align-center justify-center">
              <v-icon size="60" color="grey lighten-1">mdi-receipt-text-outline</v-icon>
              <span class="grey--text mt-2">ยังไม่ได้เลือกสลิป</span>
            </div>
            <v-btn
              v-if="testSlipPreview"
              fab
              x-small
              color="white"
              class="zoom-fab"
              elevation="2"
              @click.stop="openZoom(testSlipPreview)"
            >
              <v-icon color="#059669">mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </div>
        </v-col>

        <v-col cols="12" md="7">
          <v-file-input
            v-model="testSlipFile"
            label="เลือกไฟล์สลิป (PNG/JPG)"
            accept="image/png, image/jpeg, image/webp"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-receipt"
            show-size
            :clearable="true"
            @change="onTestSlipSelected"
          ></v-file-input>

          <v-btn
            color="#059669"
            dark
            :loading="learnLoading"
            :disabled="!testSlipFile"
            @click="learnFromSlip"
          >
            <v-icon left>mdi-brain</v-icon>
            ทดสอบและเรียนรู้ผู้รับ
          </v-btn>

          <div v-if="learnResult" class="mt-4">
            <v-alert type="success" text dense border="left" class="mb-0">
              <div class="text-body-2">
                <div><strong>เรียนรู้สำเร็จ</strong></div>
                <ul class="mb-0 mt-1">
                  <li v-if="learnResult.accountName">ชื่อ: <strong>{{ learnResult.accountName }}</strong></li>
                  <li v-if="learnResult.accountNumber">เลขที่/Biller: <strong>{{ learnResult.accountNumber }}</strong></li>
                  <li v-if="learnResult.accountType">ประเภท: <code>{{ learnResult.accountType }}</code></li>
                </ul>
              </div>
            </v-alert>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="zoomDialog" max-width="640" @click:outside="zoomDialog = false">
      <v-card class="pa-0">
        <div class="d-flex justify-end pa-2">
          <v-btn icon @click="zoomDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="pa-4 pt-0 text-center" style="background:#fff;">
          <img :src="zoomSrc" alt="QR" style="max-width:100%; max-height:80vh; display:block; margin:0 auto;" />
        </div>
        <div class="text-center caption grey--text pb-3">กดพื้นที่ว่างนอกรูปเพื่อปิด</div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDelete" max-width="420">
      <v-card>
        <v-card-title>ยืนยันการลบ?</v-card-title>
        <v-card-text>
          เมื่อลบแล้ว ระบบจะกลับไปใช้ QR Code ที่สร้างอัตโนมัติจาก PromptPay ID
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDelete = false">ยกเลิก</v-btn>
          <v-btn color="error" depressed :loading="qrLoading" @click="deleteQR">ลบ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      </v-col>
      <v-col cols="12" md="4">
    <!-- Verification summary -->
    <v-card class="pa-6 rounded-lg summary-sticky" elevation="2">
      <div class="d-flex align-center mb-4">
        <v-icon color="#7c3aed" size="32" class="mr-3">mdi-shield-check-outline</v-icon>
        <h2 class="text-h5 font-weight-bold">ระบบตรวจสอบสลิปปัจจุบันใช้</h2>
      </div>

      <v-chip :color="verificationSummary.modeColor" dark small class="mb-1">
        {{ verificationSummary.mode }}
      </v-chip>
      <div class="text-caption grey--text mb-4">{{ verificationSummary.source }}</div>

      <v-list dense class="pa-0">
        <v-list-item v-for="(check, i) in verificationSummary.checks" :key="i" class="px-0" style="min-height:32px;">
          <v-list-item-icon class="mr-2 my-auto">
            <v-icon :color="check.color" small>{{ check.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="text-body-2">{{ check.label }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AdminSettingsService from "@/services/api/adminSettings";

export default {
  name: "AdminPaymentSettings",
  data() {
    return {
      valid: false,
      loading: false,
      qrLoading: false,
      qrFile: null,
      qrFilePreview: null,
      confirmDelete: false,
      learnLoading: false,
      testSlipFile: null,
      testSlipPreview: null,
      learnResult: null,
      zoomDialog: false,
      zoomSrc: "",
      settings: {
        promptPayId: "",
        firstNameTh: "",
        lastNameTh: "",
        qrCodeImage: "",
        qrLearnedAccountType: "",
        qrLearnedAccountNumber: "",
        qrLearnedAccountName: "",
        billerRef1: "",
        billerRef2: ""
      },
      snackbar: {
        show: false,
        text: "",
        color: "success"
      }
    };
  },
  computed: {
    apiBase() {
      return (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
    },
    qrImagePreview() {
      if (this.qrFilePreview) return this.qrFilePreview;
      if (!this.settings.qrCodeImage) return null;
      const path = this.settings.qrCodeImage.startsWith("/")
        ? this.settings.qrCodeImage
        : "/" + this.settings.qrCodeImage;
      return this.apiBase + path;
    },
    verificationSummary() {
      const s = this.settings;
      const ok   = (label) => ({ icon: "mdi-check-circle",        color: "success", label });
      const skip = (label) => ({ icon: "mdi-minus-circle-outline", color: "grey",    label });
      const warn = (label) => ({ icon: "mdi-alert-circle",         color: "warning", label });

      if (!s.promptPayId && !s.qrCodeImage) {
        return {
          mode: "ยังไม่ตั้งค่า",
          modeColor: "error",
          source: "กรุณาตั้งค่า PromptPay ID หรืออัปโหลด QR Code",
          checks: [],
        };
      }

      if (s.qrCodeImage) {
        if (s.qrLearnedAccountType || s.qrLearnedAccountNumber) {
          const isBiller = s.qrLearnedAccountType === "03001";

          if (isBiller) {
            return {
              mode: "QR อัปโหลด — Bill Payment (Biller ID)",
              modeColor: "success",
              source: "ตรวจผู้รับเองในระบบ (ไม่ผ่าน Slip2Go checkReceiver)",
              checks: [
                ok(`Biller ID (last 4): ${s.qrLearnedAccountNumber || "-"}`),
                s.billerRef1 ? ok(`Ref1: "${s.billerRef1}"`) : skip("Ref1: ยังไม่ได้เรียนรู้"),
                s.billerRef2 ? ok(`Ref2: "${s.billerRef2}"`) : skip("Ref2: ยังไม่ได้เรียนรู้"),
                ok("จำนวนเงิน: ตรวจ exact (±0.001 บาท)"),
                skip("ชื่อ-นามสกุล: ไม่ตรวจ"),
              ],
            };
          }

          const typeLabel =
            s.qrLearnedAccountType === "02001" ? "เบอร์โทร"
            : s.qrLearnedAccountType === "02003" ? "เลขบัตรประชาชน"
            : s.qrLearnedAccountType === "02002" ? "E-Wallet"
            : s.qrLearnedAccountType || "PromptPay";
          const nameCheck = s.qrLearnedAccountName
            ? ok(`ชื่อผู้รับ: "${s.qrLearnedAccountName}" (ส่งให้ Slip2Go + local)`)
            : skip("ชื่อผู้รับ: ไม่ตรวจ (ยังไม่มีชื่อที่เรียนรู้)");
          return {
            mode: "QR อัปโหลด — PromptPay",
            modeColor: "success",
            source: "ส่ง checkReceiver ให้ Slip2Go + ตรวจ local ซ้ำ",
            checks: [
              ok(`ประเภทบัญชี: ${s.qrLearnedAccountType} — ${typeLabel}`),
              ok(`เลขบัญชี / PromptPay: ${s.qrLearnedAccountNumber || "-"}`),
              ok("จำนวนเงิน: ตรวจ exact (±0.001 บาท)"),
              nameCheck,
            ],
          };
        }

        return {
          mode: "QR อัปโหลด — ยังไม่ได้เรียนรู้ผู้รับ",
          modeColor: "warning",
          source: "สลิปแรกที่ผ่านจะถูกใช้เรียนรู้ผู้รับ — ยังไม่ตรวจผู้รับ",
          checks: [
            warn("ผู้รับ: ยังไม่ตรวจ (learning pass)"),
            ok("จำนวนเงิน: ตรวจ exact (±0.001 บาท)"),
            skip("ชื่อ-นามสกุล: ไม่ตรวจ"),
          ],
        };
      }

      const ppId = s.promptPayId || "";
      const accountTypeLabel =
        ppId.length === 10 ? "02001 — เบอร์โทร"
        : ppId.length === 13 ? "02003 — เลขบัตรประชาชน"
        : "01xxx — เลขบัญชีธนาคาร";
      const configuredName = [s.firstNameTh, s.lastNameTh].filter(Boolean).join(" ").trim();
      const ppNameCheck = configuredName
        ? ok(`ชื่อ-นามสกุล: "${configuredName}" (ผ่าน Slip2Go)`)
        : skip("ชื่อ-นามสกุล: ไม่ตรวจ (ยังไม่ได้ตั้งค่าชื่อผู้รับ)");
      return {
        mode: "PromptPay ID",
        modeColor: "primary",
        source: "ส่ง checkReceiver ให้ Slip2Go ตรวจฝั่ง API",
        checks: [
          ok(`เลข PromptPay: ${ppId} (${accountTypeLabel})`),
          ok("จำนวนเงิน: ตรวจ exact (±0.001 บาท)"),
          skip("Ref1: ไม่ตรวจ"),
          skip("Ref2: ไม่ตรวจ"),
          ppNameCheck,
        ],
      };
    },
  },
  mounted() {
    this.fetchSettings();
  },
  methods: {
    async fetchSettings() {
      this.loading = true;
      try {
        const res = await AdminSettingsService.getPaymentSettings();
        if (res.data && res.data.data) {
          const data = res.data.data;
          this.settings.promptPayId = data.promptPayId || "";
          this.settings.firstNameTh = data.firstNameTh || "";
          this.settings.lastNameTh = data.lastNameTh || "";
          this.settings.qrCodeImage = data.qrCodeImage || "";
          this.settings.qrLearnedAccountType = data.qrLearnedAccountType || "";
          this.settings.qrLearnedAccountNumber = data.qrLearnedAccountNumber || "";
          this.settings.qrLearnedAccountName = data.qrLearnedAccountName || "";
          this.settings.billerRef1 = data.billerRef1 || "";
          this.settings.billerRef2 = data.billerRef2 || "";
        }
      } catch (error) {
        console.error("fetchSettings error:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const res = await AdminSettingsService.updatePaymentSettings({
          promptPayId: this.settings.promptPayId,
          firstNameTh: this.settings.firstNameTh,
          lastNameTh: this.settings.lastNameTh
        });
        if (res.data && res.data.success) {
          this.showSnackbar("บันทึกการตั้งค่าเรียบร้อยแล้ว", "success");
        }
      } catch (error) {
        console.error("saveSettings error:", error);
        this.showSnackbar(error.response?.data?.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },
    onFileSelected(file) {
      if (!file) {
        this.qrFilePreview = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => { this.qrFilePreview = e.target.result; };
      reader.readAsDataURL(file);
    },
    async uploadQR() {
      if (!this.qrFile) return;
      this.qrLoading = true;
      try {
        const res = await AdminSettingsService.uploadQRCode(this.qrFile);
        if (res.data && res.data.success) {
          const data = res.data.data || {};
          this.settings.qrCodeImage = data.qrCodeImage || "";
          this.settings.qrLearnedAccountType = data.qrLearnedAccountType || "";
          this.settings.qrLearnedAccountNumber = data.qrLearnedAccountNumber || "";
          this.settings.qrLearnedAccountName = data.qrLearnedAccountName || "";
          this.qrFile = null;
          this.qrFilePreview = null;
          this.showSnackbar(res.data.message || "อัปโหลดรูป QR Code สำเร็จ", "success");
        }
      } catch (error) {
        console.error("uploadQR error:", error);
        this.showSnackbar(error.response?.data?.message || "อัปโหลดไม่สำเร็จ", "error");
      } finally {
        this.qrLoading = false;
      }
    },
    openZoom(src) {
      if (!src) return;
      this.zoomSrc = src;
      this.zoomDialog = true;
    },
    async saveLearnedReceiver() {
      this.loading = true;
      try {
        const res = await AdminSettingsService.updatePaymentSettings({
          qrLearnedAccountType: this.settings.qrLearnedAccountType || "",
          qrLearnedAccountNumber: this.settings.qrLearnedAccountNumber || "",
          qrLearnedAccountName: this.settings.qrLearnedAccountName || ""
        });
        if (res.data && res.data.success) {
          this.showSnackbar("บันทึกข้อมูลผู้รับเรียบร้อยแล้ว", "success");
        }
      } catch (error) {
        console.error("saveLearnedReceiver error:", error);
        this.showSnackbar(error.response?.data?.message || "บันทึกไม่สำเร็จ", "error");
      } finally {
        this.loading = false;
      }
    },
    onTestSlipSelected(file) {
      if (!file) {
        this.testSlipPreview = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => { this.testSlipPreview = e.target.result; };
      reader.readAsDataURL(file);
    },
    async learnFromSlip() {
      if (!this.testSlipFile) return;
      this.learnLoading = true;
      this.learnResult = null;
      try {
        const res = await AdminSettingsService.learnReceiverFromSlip(this.testSlipFile);
        if (res.data && res.data.success) {
          const data = res.data.data || {};
          this.settings.qrLearnedAccountType = data.qrLearnedAccountType || "";
          this.settings.qrLearnedAccountNumber = data.qrLearnedAccountNumber || "";
          this.settings.qrLearnedAccountName = data.qrLearnedAccountName || "";
          this.settings.billerRef1 = data.billerRef1 || "";
          this.settings.billerRef2 = data.billerRef2 || "";
          this.learnResult = res.data.extracted || null;
          this.showSnackbar(res.data.message || "เรียนรู้ผู้รับสำเร็จ", "success");
        }
      } catch (error) {
        console.error("learnFromSlip error:", error);
        this.showSnackbar(error.response?.data?.message || "ทดสอบไม่สำเร็จ", "error");
      } finally {
        this.learnLoading = false;
      }
    },
    async deleteQR() {
      this.qrLoading = true;
      try {
        const res = await AdminSettingsService.deleteQRCode();
        if (res.data && res.data.success) {
          this.settings.qrCodeImage = "";
          this.settings.qrLearnedAccountType = "";
          this.settings.qrLearnedAccountNumber = "";
          this.settings.qrLearnedAccountName = "";
          this.confirmDelete = false;
          this.showSnackbar("ลบรูป QR Code สำเร็จ", "success");
        }
      } catch (error) {
        console.error("deleteQR error:", error);
        this.showSnackbar(error.response?.data?.message || "ลบไม่สำเร็จ", "error");
      } finally {
        this.qrLoading = false;
      }
    },
    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
.admin-payment-settings {
  max-width: 1200px;
  margin: 0 auto;
}
.summary-sticky {
  position: sticky;
  top: 24px;
}
.qr-preview-box {
  position: relative;
  width: 100%;
  max-width: 260px;
  min-height: 240px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.qr-preview-box.clickable {
  cursor: zoom-in;
}
.qr-preview-box.clickable:hover {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}
.zoom-fab {
  position: absolute !important;
  right: 8px;
  bottom: 8px;
}
.empty-preview {
  width: 100%;
  height: 220px;
}
</style>
